import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
// mantine
import { Divider, Switch, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
// components
import { useDisclosure } from '@mantine/hooks';
import ProjectSelect from '@/modules/Projects/components/ProjectSelect';
import ExportAgentButton from '@/modules/SettingsDrawer/components/ExportAgentButton';
import ImportAgentForm from '@/modules/SettingsDrawer/components/ImportAgentForm';
import PublishProjectButton from '@/modules/Projects/components/PublishProjectButton';
import CustomerImportExport from '@/modules/SettingsDrawer/components/CustomerImportExport';
// utils
import { useProjectsQuery } from '@/services/designer/projects/projects.queries';
import { getProjectName } from '@/modules/Projects/Project.utils';
import { useFeatureFlag, FF_PROJECTS_V2 } from '@/hooks/useFeatureFlag';

const handleImport = () => {
    notifications.show({
        color: 'green',
        message: 'Agent imported successfully'
    });
};

const AgentManagement: FC = () => {
    const { projectId: projectIdParam } = useParams();
    const [projectId, setProjectId] = useState<string>(projectIdParam ?? '');
    const [isV2Enabled, { toggle }] = useDisclosure(true);
    const { enabled: isProjectsV2, isResolved } =
        useFeatureFlag(FF_PROJECTS_V2);
    const { data: projects = [] } = useProjectsQuery(false, {
        enabled: isResolved && !isProjectsV2
    });

    useEffect(() => {
        if (projects.length > 0 && !projectId) {
            setProjectId(projects[0].id);
        }
    }, [projects, projectId]);

    const handleProjectChange = (value: string | null) => {
        if (value) {
            setProjectId(value);
        }
    };

    const project = projects.find((p) => p.id === projectId);

    if (!isResolved) {
        return;
    }

    if (isProjectsV2) {
        return <CustomerImportExport />;
    }

    return (
        <>
            {!isV2Enabled && (
                <>
                    <ProjectSelect
                        className="max-w-md"
                        label="Select Project"
                        placeholder="Select Project"
                        size="sm"
                        value={projectId}
                        onChange={handleProjectChange}
                    />
                    <Divider className="my-4" />

                    <Text fs="xs" fw={600}>
                        Publish
                    </Text>
                    <div className="mb-2 text-sm text-gray-6">
                        Publish Agent components including Flows, AKB and
                        Attributes
                    </div>
                    <PublishProjectButton project={project}>
                        Publish &quot;{getProjectName(project)}&quot; project
                    </PublishProjectButton>

                    <Divider className="my-4" />
                </>
            )}
            <Switch
                checked={isV2Enabled}
                className="mb-2"
                label="Use V2 API"
                onChange={toggle}
            />
            <Text fs="xs" fw={600}>
                Export
            </Text>
            <div className="mb-2 text-sm text-gray-6">
                Export Agent to `.zip` archive containing Flows, AKB and
                Attributes
            </div>
            <ExportAgentButton
                isV2Enabled={isV2Enabled}
                projectId={projectId}
            />

            <Divider className="my-4" />

            <Text fs="sm" fw={600}>
                Import
            </Text>
            <div className="mb-2 text-sm text-gray-6">
                Import Agent from `.zip` archive containing Flows, AKB and
                Attributes
            </div>
            <ImportAgentForm
                isV2Enabled={isV2Enabled}
                projectId={projectId}
                onSubmit={handleImport}
            />
        </>
    );
};

export default AgentManagement;
